import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { SessionStatus } from '@weremote/shared';

export interface SessionRow {
  id: string;
  status: SessionStatus;
  startedAt: string;
  endedAt: string | null;
  durationSec: number | null;
  endReason: string | null;
  relayUsed: boolean;
  technicianIp: string | null;
  bytesIn: string;     // BigInt-as-string
  bytesOut: string;
  technician: { id: string; email: string; firstName: string | null; lastName: string | null } | null;
  device: { id: string; rustdeskId: string; alias: string | null; hostname: string | null };
}

interface PaginatedSessions {
  data: SessionRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SessionFilters {
  page?: number;
  pageSize?: number;
  status?: SessionStatus;
  technicianId?: string;
  deviceId?: string;
  fromDate?: string;
  toDate?: string;
}

export function useSessions(filters: SessionFilters) {
  return useQuery({
    queryKey: ['sessions', filters],
    queryFn: () =>
      api.get<PaginatedSessions>('/sessions', {
        query: {
          page: filters.page ?? 1,
          pageSize: filters.pageSize ?? 25,
          status: filters.status,
          technicianId: filters.technicianId,
          deviceId: filters.deviceId,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
        },
      }),
    placeholderData: (prev) => prev,
  });
}

export interface SessionDetail extends SessionRow {
  events: {
    id: string;
    type: string;
    timestamp: string;
    data: unknown;
  }[];
}

export function useSession(id: string | undefined) {
  return useQuery({
    queryKey: ['sessions', id],
    queryFn: () => api.get<SessionDetail>(`/sessions/${id}`),
    enabled: !!id,
  });
}

/** Used by the live page to invalidate the list when SSE events arrive. */
export function useInvalidateSessions() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['sessions'] });
}
